import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/shared/lib/utils";

/** Gombok a design-rendszer 6. fejezete szerint:
 *  primary = arany alap + sötétkék felirat; outline = 1,5 px arany keret + gold-text;
 *  outlineInk = 1 px ink keret; link = gold-text, aláhúzás nélkül. */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-gold text-ink hover:bg-gold-hover active:bg-gold-active",
        outline: "border-[1.5px] border-gold bg-white text-gold-text hover:bg-cream",
        outlineInk: "border border-ink bg-white text-ink hover:bg-paper",
        ghost: "text-ink hover:bg-gray",
        link: "h-auto px-0 text-gold-text hover:text-[#5E4B18]",
        destructive: "bg-red text-white hover:opacity-90",
      },
      size: {
        lg: "h-[50px] px-5 text-[16px]",
        md: "h-[46px] px-4 text-[15px]",
        sm: "h-9 rounded-sm px-3.5 text-[13px]",
        icon: "h-11 w-11",
      },
      block: { true: "w-full", false: "" },
    },
    defaultVariants: { variant: "primary", size: "md", block: false },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, block, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : (type ?? "button")}
        className={cn(buttonVariants({ variant, size, block }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
